'use client';

import Icon from '@/components/ui/AppIcon';

interface BookingsListProps {
  dateRange: string;
}

const BookingsList = ({ dateRange }: BookingsListProps) => {
  // Demo bookings data
  const bookings = [
    {
      id: 'BK-2026-001',
      type: 'flight',
      partner: 'Skyscanner',
      route: 'London (LHR) → Dubai (DXB)',
      customer: 'John Smith',
      bookingDate: '2026-02-05',
      amount: '£456.00',
      commission: '£18.24',
      status: 'confirmed',
    },
    {
      id: 'BK-2026-002',
      type: 'hotel',
      partner: 'Booking.com',
      route: 'Hilton Dubai Creek, 3 nights',
      customer: 'Sarah Johnson',
      bookingDate: '2026-02-04',
      amount: '£320.00',
      commission: '£12.80',
      status: 'confirmed',
    },
    {
      id: 'BK-2026-003',
      type: 'holiday',
      partner: 'Expedia',
      route: 'Maldives Package, 7 days',
      customer: 'Michael Brown',
      bookingDate: '2026-02-03',
      amount: '£1,850.00',
      commission: '£74.00',
      status: 'pending',
    },
    {
      id: 'BK-2026-004',
      type: 'flight',
      partner: 'Skyscanner',
      route: 'Manchester (MAN) → New York (JFK)',
      customer: 'Emma Wilson',
      bookingDate: '2026-02-02',
      amount: '£589.00',
      commission: '£23.56',
      status: 'confirmed',
    },
    {
      id: 'BK-2026-005',
      type: 'hotel',
      partner: 'Booking.com',
      route: 'Marriott Times Square, 5 nights',
      customer: 'David Lee',
      bookingDate: '2026-02-01',
      amount: '£780.00',
      commission: '£31.20',
      status: 'confirmed',
    },
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'flight':
        return 'GlobeAltIcon';
      case 'hotel':
        return 'BuildingOffice2Icon';
      case 'holiday':
        return 'SparklesIcon';
      default:
        return 'TicketIcon';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'flight':
        return 'bg-blue-50 text-blue-600';
      case 'hotel':
        return 'bg-green-50 text-green-600';
      case 'holiday':
        return 'bg-purple-50 text-purple-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg text-foreground">Recent Bookings</h3>
        <button className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-primary hover:bg-primary/5 rounded-lg transition-colors">
          <Icon name="FunnelIcon" size={16} />
          <span>Filter</span>
        </button>
      </div>

      <div className="space-y-3">
        {bookings.map((booking) => (
          <div
            key={booking.id}
            className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${getTypeColor(booking.type)}`}>
                  <Icon name={getTypeIcon(booking.type) as any} size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-bold text-foreground">{booking.id}</h4>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded ${
                        booking.status === 'confirmed' ?'bg-green-100 text-green-700' :'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {booking.status}
                    </span>
                  </div>
                  <p className="text-sm text-foreground mb-1">{booking.route}</p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="UserCircleIcon" size={14} />
                      {booking.customer}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="CalendarIcon" size={14} />
                      {new Date(booking.bookingDate).toLocaleDateString('en-GB')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="BuildingOffice2Icon" size={14} />
                      {booking.partner}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6 lg:flex-col lg:items-end">
                <div className="text-left lg:text-right">
                  <p className="text-xs text-muted-foreground mb-1">Booking Value</p>
                  <p className="font-bold text-foreground">{booking.amount}</p>
                </div>
                <div className="text-left lg:text-right">
                  <p className="text-xs text-muted-foreground mb-1">Your Commission</p>
                  <p className="font-black text-green-600">{booking.commission}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button className="w-full py-3 text-sm font-bold text-primary hover:bg-primary/5 rounded-lg transition-colors border border-border">
        Load More Bookings
      </button>
    </div>
  );
};

export default BookingsList;
